import { cn } from "@/components/ui/utils";
import { Orb, type AgentState } from "@/components/ui/orb";
import type { VoiceStatus } from "@/lib/voice/conversation";

const LABELS: Record<VoiceStatus, string> = {
  connecting: "Connecting",
  error: "Connection problem",
  idle: "Ready",
  listening: "Listening",
  speaking: "Speaking",
};

const AGENT_STATE_MAP: Record<VoiceStatus, AgentState> = {
  idle: null,
  connecting: "thinking",
  listening: "listening",
  speaking: "talking",
  error: null,
};

/**
 * Per-status classes. `orb`, `glow` and `halo` follow the effective status
 * (typing reads as connecting). `motion` follows the voice status itself, so a
 * speaking aurora keeps its scale while the user types.
 */
const STATUS_STYLE: Record<
  VoiceStatus,
  { orb: string; glow: string; halo: string; motion: string }
> = {
  idle: {
    orb: "[--orb-a:var(--viz-7)] [--orb-b:var(--viz-6)]",
    glow: "shadow-[var(--shadow-glow-soft,var(--elevation-lg))] ring-1 ring-accent-strong/30",
    halo: "bg-gradient-to-tr from-accent-strong/25 to-transparent",
    motion: "",
  },
  connecting: {
    orb: "[--orb-a:var(--viz-8)] [--orb-b:var(--viz-6)]",
    glow: "shadow-[var(--shadow-glow-accent,var(--elevation-xl))] ring-2 ring-processing/40",
    halo: "bg-gradient-to-tr from-processing/40 to-transparent opacity-80 animate-[spin_6s_linear_infinite]",
    motion: "",
  },
  listening: {
    orb: "[--orb-a:var(--viz-7)] [--orb-b:var(--viz-5)]",
    glow: "shadow-[var(--shadow-glow-accent,var(--elevation-xl))] ring-2 ring-info/60 scale-[1.03]",
    halo: "bg-gradient-to-tr from-info/40 to-transparent opacity-90",
    motion: "animate-[pulse_2.2s_ease-in-out_infinite]",
  },
  speaking: {
    orb: "[--orb-a:var(--success-solid)] [--orb-b:var(--viz-7)]",
    glow: "shadow-[var(--shadow-glow-accent,var(--elevation-xl))] ring-2 ring-success/60",
    halo: "bg-gradient-to-tr from-success/45 to-transparent opacity-100",
    motion: "scale-105 animate-[pulse_1.2s_ease-in-out_infinite]",
  },
  error: {
    orb: "[--orb-a:var(--danger-solid)] [--orb-b:var(--warning-solid)]",
    glow: "shadow-lg ring-2 ring-destructive *:opacity-70 *:grayscale-[.55]",
    halo: "bg-destructive/20",
    motion: "",
  },
};

export function VoiceAurora({
  className,
  isTyping = false,
  status,
}: {
  className?: string;
  isTyping?: boolean;
  status: VoiceStatus;
}) {
  const typing = isTyping && status !== "error";
  const effectiveStatus = typing ? "connecting" : status;
  const effectiveAgentState: AgentState = typing ? "thinking" : AGENT_STATE_MAP[status];
  const { orb, glow, halo } = STATUS_STYLE[effectiveStatus];

  return (
    <div
      aria-label={`Voice assistant: ${LABELS[status]}`}
      className={cn(
        "group relative aspect-square overflow-hidden rounded-full transition-all duration-700 ease-out motion-reduce:transform-none motion-reduce:animate-none motion-reduce:transition-none",
        orb,
        glow,
        STATUS_STYLE[status].motion,
        effectiveStatus === "connecting" && "animate-pulse",
        className,
      )}
      role="img"
    >
      <div
        aria-hidden="true"
        className={cn(
          "pointer-events-none absolute -inset-1 rounded-full opacity-60 blur-md transition-opacity duration-700 motion-reduce:hidden",
          halo,
        )}
      />

      <Orb agentState={effectiveAgentState} className="size-full" />

      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 rounded-full bg-gradient-to-b from-primary-foreground/30 via-transparent to-foreground/20 opacity-80 shadow-[var(--surface-inner-highlight),var(--surface-inset-shadow)]"
      />

      <div
        aria-hidden="true"
        className="pointer-events-none absolute -top-1/4 -left-1/4 size-3/4 rounded-full bg-radial from-primary-foreground/35 via-primary-foreground/10 to-transparent blur-xs opacity-70 motion-reduce:hidden"
      />
    </div>
  );
}
